import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle, MapPin, Barcode, Package, Calendar, Trash2, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HistoryDetailPage() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [pickingList, setPickingList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilteredList, setShowFilteredList] = useState(false);

  useEffect(() => {
    fetchPickingList();
  }, [listId]);

  const fetchPickingList = async () => {
    try {
      const response = await axios.get(`${API}/picking/picking-lists/${listId}`);
      setPickingList(response.data);
    } catch (error) {
      console.error("Error fetching picking list:", error);
      toast.error("Kunde inte ladda plocklistan");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/picking/picking-lists/${listId}`);
      toast.success("Plocklista borttagen");
      navigate("/history");
    } catch (error) {
      toast.error("Kunde inte ta bort plocklistan");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group items by order
  const groupByOrder = (items) => {
    const groups = {};
    items.forEach(item => {
      const order = item.ordernummer || 'Okänd order';
      if (!groups[order]) {
        groups[order] = [];
      }
      groups[order].push(item);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-[50vh]">
          <p className="text-muted-foreground">Laddar...</p>
        </div>
      </div>
    );
  }

  if (!pickingList) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-[50vh]">
          <p className="text-destructive">Plocklista hittades inte</p>
        </div>
      </div>
    );
  }

  const orderGroups = groupByOrder(pickingList.items);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                data-testid="back-btn"
                onClick={() => navigate('/history')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight" data-testid="detail-title">
                  {pickingList.name}
                </h1>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDate(pickingList.completed_at || pickingList.created_at)}
                </p>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" data-testid="delete-btn">
                  <Trash2 className="h-5 w-5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Ta bort plocklista?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Denna åtgärd kan inte ångras. Plocklistan och all historik kommer att tas bort permanent.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Ta bort
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      {/* Summary Cards */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-3 gap-4 mb-6" data-testid="summary-cards">
          <Card 
            className="stat-tile cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => { setStatusFilter('all'); setShowFilteredList(true); }}
            data-testid="stat-total"
          >
            <CardContent className="p-4 text-center">
              <Package className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-3xl font-bold font-heading">{pickingList.total_items}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Totalt</p>
            </CardContent>
          </Card>
          <Card 
            className="stat-tile cursor-pointer hover:border-success/50 transition-colors"
            onClick={() => { setStatusFilter('picked'); setShowFilteredList(true); }}
            data-testid="stat-picked"
          >
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 mx-auto mb-2 text-success" />
              <p className="text-3xl font-bold font-heading text-success">{pickingList.picked_count || 0}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Plockade</p>
            </CardContent>
          </Card>
          <Card 
            className="stat-tile cursor-pointer hover:border-amber-500/50 transition-colors"
            onClick={() => { setStatusFilter('skipped'); setShowFilteredList(true); }}
            data-testid="stat-skipped"
          >
            <CardContent className="p-4 text-center">
              <SkipForward className="h-6 w-6 mx-auto mb-2 text-amber-500" />
              <p className="text-3xl font-bold font-heading text-amber-500">{pickingList.skipped_count || 0}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Överhoppade</p>
            </CardContent>
          </Card>
        </div>

        {/* Items grouped by order */}
        <div className="space-y-6" data-testid="order-groups">
          {Object.entries(orderGroups).map(([orderNumber, items]) => (
            <Card key={orderNumber} className="overflow-hidden">
              <CardHeader className="bg-secondary/50 py-3">
                <CardTitle className="font-heading text-base uppercase tracking-wide flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Order: {orderNumber}
                  <Badge variant="outline" className="ml-auto">
                    {items.length} {items.length === 1 ? 'produkt' : 'produkter'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={`p-4 ${item.status === 'skipped' ? 'bg-amber-500/5' : ''}`}
                      data-testid={`item-${item.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {item.status === 'picked' && (
                              <CheckCircle className="h-4 w-4 text-success shrink-0" />
                            )}
                            {item.status === 'skipped' && (
                              <SkipForward className="h-4 w-4 text-amber-500 shrink-0" />
                            )}
                            <h4 className="font-heading font-bold uppercase tracking-tight">
                              {item.produktnamn}
                            </h4>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mt-2">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Barcode className="h-3 w-3" />
                              <span className="font-mono">{item.artikelnummer}</span>
                            </div>
                            {item.ean && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <span className="text-xs">EAN:</span>
                                <span className="font-mono">{item.ean}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span className="font-mono">{item.lagerplats}</span>
                            </div>
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Package className="h-3 w-3" />
                              <span>{item.antal} st</span>
                            </div>
                            {item.status === 'picked' && item.picked_location && (
                              <div className="flex items-center gap-1 text-success">
                                <CheckCircle className="h-3 w-3" />
                                <span className="font-mono">Från: {item.picked_location}</span>
                              </div>
                            )}
                          </div>
                          {item.kommentar && (
                            <p className="text-sm text-muted-foreground mt-2 italic">
                              {item.kommentar}
                            </p>
                          )}
                        </div>
                        <Badge 
                          variant={item.status === 'picked' ? 'default' : 'outline'} 
                          className={`shrink-0 ${item.status === 'skipped' ? 'border-amber-500 text-amber-500' : ''}`}
                        >
                          {item.status === 'picked' ? 'Plockad' : 'Överhoppad'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Filtered Status List Dialog */}
      <Dialog open={showFilteredList} onOpenChange={setShowFilteredList}>
        <DialogContent className="max-w-3xl max-h-[80vh]" data-testid="filtered-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              {statusFilter === 'all' && <><Package className="h-5 w-5" /> Alla produkter</>}
              {statusFilter === 'picked' && <><CheckCircle className="h-5 w-5 text-success" /> Plockade produkter</>}
              {statusFilter === 'skipped' && <><SkipForward className="h-5 w-5 text-amber-500" /> Överhoppade produkter</>}
            </DialogTitle>
            <DialogDescription>
              {statusFilter === 'all' && `${pickingList.total_items} produkter totalt`}
              {statusFilter === 'picked' && `${pickingList.picked_count || 0} produkter plockade`}
              {statusFilter === 'skipped' && `${pickingList.skipped_count || 0} produkter överhoppade`}
            </DialogDescription>
          </DialogHeader>
          
          {/* Filter tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b pb-3">
            <Button 
              variant={statusFilter === 'all' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setStatusFilter('all')}
            >
              Alla ({pickingList.total_items})
            </Button>
            <Button 
              variant={statusFilter === 'picked' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setStatusFilter('picked')}
              className={statusFilter === 'picked' ? 'bg-success hover:bg-success/90' : 'text-success border-success/50'}
            >
              Plockade ({pickingList.picked_count || 0})
            </Button>
            <Button 
              variant={statusFilter === 'skipped' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setStatusFilter('skipped')}
              className={statusFilter === 'skipped' ? 'bg-amber-500 hover:bg-amber-500/90' : 'text-amber-500 border-amber-500/50'}
            >
              Överhoppade ({pickingList.skipped_count || 0})
            </Button>
          </div>
          
          <ScrollArea className="max-h-[55vh] pr-4">
            <div className="space-y-2">
              {pickingList.items
                .filter(item => {
                  if (statusFilter === 'all') return true;
                  return item.status === statusFilter;
                })
                .map((item) => (
                <div
                  key={item.id}
                  className={`p-3 rounded-md border transition-all ${
                    item.status === 'picked' ? 'bg-success/5 border-success/30' :
                    item.status === 'skipped' ? 'bg-amber-500/5 border-amber-500/30' :
                    'bg-card border-border'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {item.status === 'picked' && <CheckCircle className="h-4 w-4 text-success" />}
                        {item.status === 'skipped' && <SkipForward className="h-4 w-4 text-amber-500" />}
                        <span className="font-medium">{item.produktnamn}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                        <span className="font-mono">{item.artikelnummer}</span>
                        {item.ean && <span className="font-mono text-xs">EAN: {item.ean}</span>}
                        <span>{item.antal} st</span>
                        <span className="font-mono">{item.lagerplats || '—'}</span>
                        {item.ordernummer && <span>Order: {item.ordernummer}</span>}
                        {item.status === 'picked' && item.picked_location && (
                          <span className="text-success">Plockad från: {item.picked_location}</span>
                        )}
                      </div>
                    </div>
                    <Badge 
                      variant={item.status === 'picked' ? 'default' : 'outline'}
                      className={item.status === 'skipped' ? 'border-amber-500 text-amber-500' : ''}
                    >
                      {item.status === 'picked' ? 'Plockad' : 'Överhoppad'}
                    </Badge>
                  </div>
                </div>
              ))}
              {pickingList.items.filter(item => statusFilter === 'all' ? true : item.status === statusFilter).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Inga produkter med denna status
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
